import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useWallet } from './WalletContext';

const ProContext = createContext(null);

const SESSION_KEY = 'poolscope_pro_session';

export function ProProvider({ children }) {
  const { account, signer, isConnected } = useWallet();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  // Restore session from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(SESSION_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Check if token is expired
        if (parsed.expiresAt && Date.now() < parsed.expiresAt) {
          setSession(parsed);
        } else {
          localStorage.removeItem(SESSION_KEY);
        }
      }
    } catch {
      localStorage.removeItem(SESSION_KEY);
    }
    setLoading(false);
  }, []);

  // Clear session when wallet disconnects or changes
  useEffect(() => {
    if (!isConnected) {
      setSession(null);
      localStorage.removeItem(SESSION_KEY);
    } else if (session && account && session.address?.toLowerCase() !== account?.toLowerCase()) {
      setSession(null);
      localStorage.removeItem(SESSION_KEY);
    }
  }, [isConnected, account, session]);

  // Verify session with backend
  const verifySession = useCallback(async (token) => {
    try {
      const res = await fetch('/api/auth/verify', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return null;
      return res.json();
    } catch {
      return null;
    }
  }, []);

  // Sign in with Ethereum (SIWE)
  const signIn = useCallback(async () => {
    if (!signer || !account) {
      throw new Error('Wallet not connected');
    }

    // Get nonce from server
    const nonceRes = await fetch('/api/auth/session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ address: account }),
    });

    if (!nonceRes.ok) throw new Error('Failed to get nonce');
    const { nonce, message: siweMessage } = await nonceRes.json();

    // Sign the SIWE message
    const signature = await signer.signMessage(siweMessage || createSIWEMessage(account, nonce));

    // Verify signature and get JWT
    const verifyRes = await fetch('/api/auth/session', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ address: account, signature, nonce }),
    });

    if (!verifyRes.ok) throw new Error('Signature verification failed');
    const data = await verifyRes.json();

    const sessionData = {
      token: data.token,
      address: account,
      tier: data.tier || 'free',
      expiresAt: data.expiresAt || Date.now() + 7 * 24 * 60 * 60 * 1000,
    };

    setSession(sessionData);
    localStorage.setItem(SESSION_KEY, JSON.stringify(sessionData));
    return sessionData;
  }, [signer, account]);

  // Upgrade to Pro — opens Stripe Checkout or returns USDC payment info
  const upgrade = useCallback(async (method = 'stripe', { plan } = {}) => {
    const token = session?.token;
    if (!token && !account) throw new Error('Not authenticated');

    if (method === 'stripe') {
      const res = await fetch('/api/billing/checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ address: account, plan }),
      });
      if (!res.ok) throw new Error('Failed to create checkout session');
      const { url } = await res.json();
      if (!url || !url.startsWith('https://checkout.stripe.com/')) {
        throw new Error('Invalid checkout URL');
      }
      window.open(url, '_blank');
      return { method: 'stripe', url };
    }

    if (method === 'usdc') {
      const res = await fetch('/api/billing/verify-usdc', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ address: account }),
      });
      if (!res.ok) throw new Error('Failed to get USDC payment info');
      const data = await res.json();

      // If verification succeeded (txHash was provided and verified), update session tier
      if (data.verified && data.tier) {
        const updated = {
          ...session,
          tier: data.tier,
        };
        setSession(updated);
        localStorage.setItem(SESSION_KEY, JSON.stringify(updated));
      }

      return data;
    }

    throw new Error(`Unknown payment method: ${method}`);
  }, [session, account]);

  // Manage subscription
  const manageSubscription = useCallback(async () => {
    const token = session?.token;
    if (!token) throw new Error('Not authenticated');

    const res = await fetch('/api/billing/checkout', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ address: account }),
    });
    if (!res.ok) throw new Error('Failed to get billing portal');
    const { url } = await res.json();
    if (!url || !url.startsWith('https://billing.stripe.com/')) {
      throw new Error('Invalid billing portal URL');
    }
    window.open(url, '_blank');
    return url;
  }, [session, account]);

  // Refresh subscription status
  const refreshStatus = useCallback(async () => {
    if (!session?.token) return;
    const data = await verifySession(session.token);
    if (data) {
      const updated = { ...session, tier: data.tier || 'free' };
      setSession(updated);
      localStorage.setItem(SESSION_KEY, JSON.stringify(updated));
    }
  }, [session, verifySession]);

  // Dev override: ?pro=test in URL forces Pro mode (sticky for the session)
  // Dev override: ?pro=test in URL forces Pro mode (dev only, stripped in production builds)
  const devProRef = useRef(
    process.env.NODE_ENV === 'development' &&
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('pro') === 'test'
  );

  const isPro = devProRef.current || session?.tier === 'pro';

  const value = useMemo(() => ({
    isPro,
    tier: session?.tier || 'free',
    session,
    loading,
    signIn,
    upgrade,
    manageSubscription,
    refreshStatus,
  }), [isPro, session, loading, signIn, upgrade, manageSubscription, refreshStatus]);

  return (
    <ProContext.Provider value={value}>
      {children}
    </ProContext.Provider>
  );
}

export function usePro() {
  const ctx = useContext(ProContext);
  if (!ctx) throw new Error('usePro must be used within ProProvider');
  return ctx;
}

// Helper to create SIWE message
function createSIWEMessage(address, nonce) {
  const domain = typeof window !== 'undefined' ? window.location.host : 'poolscope.app';
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://poolscope.app';
  const now = new Date().toISOString();
  return `${domain} wants you to sign in with your Ethereum account:\n${address}\n\nSign in to PoolScope Pro\n\nURI: ${origin}\nVersion: 1\nChain ID: 1\nNonce: ${nonce}\nIssued At: ${now}`;
}
